// GENERATED FILE — do not edit. Source of truth: contracts/. Regenerate: node scripts/sync-contracts.mjs

import { api } from '../apiClient';

import { z } from 'zod';
// Runtime validation: every named type above has a matching `<Name>Schema` zod export — the
// exact schema the backend validates with (story 2-92), so forms can `.parse()`/`.safeParse()`
// against it instead of hand-rolling checks. `.refine()` cross-field rules are preserved
// verbatim; the refine() error MESSAGE is not — the server owns user-facing text.

export type Org = {
  id: string;
  name: string;
  slug: string;
  plan: string;
  status: string;
  memberCount: number;
  flagCount: number;
};
export const OrgSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  plan: z.string(),
  status: z.string(),
  memberCount: z.number(),
  flagCount: z.number(),
}).strict();

export type FlagSetState = "on" | "locked" | "off";
export const FlagSetStateSchema = z.enum(["on", "locked", "off"] as const);

export type FlagStateView = {
  featureKey: string;
  enabled: boolean;
  state: FlagSetState;
  plane: "platform" | "org";
  note: string | null;
};
export const FlagStateViewSchema = z.object({
  featureKey: z.string(),
  enabled: z.boolean(),
  state: FlagSetStateSchema,
  plane: z.enum(["platform", "org"] as const),
  note: z.string().nullable(),
}).strict();

export type OrgDetail = {
  id: string;
  name: string;
  slug: string;
  plan: string;
  status: string;
  entitlements: Array<string>;
  flags: Array<FlagStateView>;
};
export const OrgDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  plan: z.string(),
  status: z.string(),
  entitlements: z.array(z.string()),
  flags: z.array(FlagStateViewSchema),
}).strict();

export type Feature = {
  key: string;
  name: string;
  category: string;
  status: string;
  defaultForNewOrgs: boolean;
};
export const FeatureSchema = z.object({
  key: z.string(),
  name: z.string(),
  category: z.string(),
  status: z.string(),
  defaultForNewOrgs: z.boolean(),
}).strict();

export type SetFlagBody = {
  featureKey: string;
  enabled?: boolean | undefined;
  state?: FlagSetState | undefined;
  note?: string | null | undefined;
};
export const SetFlagBodySchema = z.object({
  featureKey: z.string().min(1),
  enabled: z.boolean().optional(),
  state: FlagSetStateSchema.optional(),
  note: z.string().nullable().optional(),
}).strict().refine((body) => body.state !== undefined || body.enabled !== undefined);

export type CreateOrgBody = {
  name: string;
  slug?: string | undefined;
  planSlug?: string | undefined;
  ownerEmail: string;
  ownerName?: string | undefined;
};
export const CreateOrgBodySchema = z.object({
  name: z.string().min(1),
  slug: z.string().optional(),
  planSlug: z.string().optional(),
  ownerEmail: z.string().email(),
  ownerName: z.string().optional(),
}).strict();

export type EmailUndeliveredWarning = {
  kind: "email_undelivered";
  message: string;
  resendAvailable: boolean;
};
export const EmailUndeliveredWarningSchema = z.object({
  kind: z.literal("email_undelivered"),
  message: z.string(),
  resendAvailable: z.boolean(),
}).strict();

export type CreateOrgResult = {
  data: OrgDetail;
  warning: EmailUndeliveredWarning | null;
};
export const CreateOrgResultSchema = z.object({
  data: OrgDetailSchema,
  warning: EmailUndeliveredWarningSchema.nullable(),
}).strict();

export type PlanLimits = Record<string, number>;
export const PlanLimitsSchema = z.record(z.number().int().min(-1));

export type PlanDriftField = {
  field: string;
  current: string;
  codeDefault: string;
};
export const PlanDriftFieldSchema = z.object({
  field: z.string(),
  current: z.string(),
  codeDefault: z.string(),
}).strict();

export type PlatformPlan = {
  slug: string;
  name: string;
  description: string | null;
  priceMonthly: number | null;
  currency: string;
  trialDays: number;
  isPublic: boolean;
  isCustom: boolean;
  active: boolean;
  limits: PlanLimits;
  entitlements: Array<string>;
  ownerEditedFields: Array<string>;
  drift: Array<PlanDriftField>;
};
export const PlatformPlanSchema = z.object({
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  priceMonthly: z.number().nullable(),
  currency: z.string(),
  trialDays: z.number(),
  isPublic: z.boolean(),
  isCustom: z.boolean(),
  active: z.boolean(),
  limits: PlanLimitsSchema,
  entitlements: z.array(z.string()),
  ownerEditedFields: z.array(z.string()),
  drift: z.array(PlanDriftFieldSchema),
}).strict();

export type UpdatePlanBody = {
  name?: string | undefined;
  description?: string | undefined;
  priceMonthly?: number | null | undefined;
  isPublic?: boolean | undefined;
  limits?: PlanLimits | undefined;
  entitlements?: Array<string> | undefined;
};
export const UpdatePlanBodySchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().optional(),
  priceMonthly: z.number().min(0).nullable().optional(),
  isPublic: z.boolean().optional(),
  limits: PlanLimitsSchema.optional(),
  entitlements: z.array(z.string().min(3)).optional(),
}).strict().refine((body) => Object.keys(body).length > 0);

export type RevertPlanFieldBody = {
  field: string;
};
export const RevertPlanFieldBodySchema = z.object({
  field: z.string().min(1),
}).strict();

export type AssignOrgPlanBody = {
  planSlug: string;
};
export const AssignOrgPlanBodySchema = z.object({
  planSlug: z.string().regex(new RegExp("^[a-z0-9-]+$"), "must be a plan slug"),
}).strict();

export type AssignOrgPlanResult = {
  data: OrgDetail;
  warning: string | null;
};
export const AssignOrgPlanResultSchema = z.object({
  data: OrgDetailSchema,
  warning: z.string().nullable(),
}).strict();

export type ListOrgsResponse = {
  items: Array<Org>;
  nextCursor: string | null;
  total?: number | undefined;
};

export const ListOrgsQuerySchema = z.object({
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  sort: z.string().min(1).optional(),
});

export async function listOrgs(query?: {
  cursor?: string | undefined;
  limit?: number | undefined;
  sort?: string | undefined;
}): Promise<ListOrgsResponse> {
  const response = await api.get<ListOrgsResponse>(`/platform/orgs`, { params: query });
  return response.data;
}

export type CreateOrgResponse = CreateOrgResult;

export async function createOrg(body: CreateOrgBody): Promise<CreateOrgResponse> {
  const response = await api.post<CreateOrgResponse>(`/platform/orgs`, body);
  return response.data;
}

export type GetOrgResponse = {
  data: OrgDetail;
};

export const GetOrgParamsSchema = z.object({
  orgId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function getOrg(orgId: string): Promise<GetOrgResponse> {
  const response = await api.get<GetOrgResponse>(`/platform/orgs/${encodeURIComponent(orgId)}`);
  return response.data;
}

export type SetOrgFlagResponse = {
  data: FlagStateView;
};

export const SetOrgFlagParamsSchema = z.object({
  orgId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function setOrgFlag(orgId: string, body: SetFlagBody): Promise<SetOrgFlagResponse> {
  const response = await api.post<SetOrgFlagResponse>(`/platform/orgs/${encodeURIComponent(orgId)}/flags`, body);
  return response.data;
}

export type ClearOrgFlagResponse = {
  data: OrgDetail;
};

export const ClearOrgFlagParamsSchema = z.object({
  orgId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
  featureKey: z.string().min(1),
});

export async function clearOrgFlag(orgId: string, featureKey: string): Promise<ClearOrgFlagResponse> {
  const response = await api.delete<ClearOrgFlagResponse>(`/platform/orgs/${encodeURIComponent(orgId)}/flags/${encodeURIComponent(featureKey)}`);
  return response.data;
}

export type AssignOrgPlanResponse = AssignOrgPlanResult;

export const AssignOrgPlanParamsSchema = z.object({
  orgId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function assignOrgPlan(orgId: string, body: AssignOrgPlanBody): Promise<AssignOrgPlanResponse> {
  const response = await api.post<AssignOrgPlanResponse>(`/platform/orgs/${encodeURIComponent(orgId)}/plan`, body);
  return response.data;
}

export type ListFeaturesResponse = {
  data: Array<Feature>;
};

export async function listFeatures(): Promise<ListFeaturesResponse> {
  const response = await api.get<ListFeaturesResponse>(`/platform/features`);
  return response.data;
}

export type ListPlansResponse = {
  data: Array<PlatformPlan>;
  metrics: Array<string>;
};

export async function listPlans(): Promise<ListPlansResponse> {
  const response = await api.get<ListPlansResponse>(`/platform/plans`);
  return response.data;
}

export type UpdatePlanResponse = {
  data: PlatformPlan;
};

export const UpdatePlanParamsSchema = z.object({
  slug: z.string().regex(new RegExp("^[a-z0-9-]+$"), "must be a plan slug"),
});

export async function updatePlan(slug: string, body: UpdatePlanBody): Promise<UpdatePlanResponse> {
  const response = await api.patch<UpdatePlanResponse>(`/platform/plans/${encodeURIComponent(slug)}`, body);
  return response.data;
}

export type RevertPlanFieldResponse = {
  data: PlatformPlan;
};

export const RevertPlanFieldParamsSchema = z.object({
  slug: z.string().regex(new RegExp("^[a-z0-9-]+$"), "must be a plan slug"),
});

export async function revertPlanField(slug: string, body: RevertPlanFieldBody): Promise<RevertPlanFieldResponse> {
  const response = await api.post<RevertPlanFieldResponse>(`/platform/plans/${encodeURIComponent(slug)}/revert`, body);
  return response.data;
}

export const platformKeys = {
  all: ["platform"] as const,
  lists: () => [...platformKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...platformKeys.lists(), ...(filters ? [filters] : [])] as const,
  details: () => [...platformKeys.all, 'detail'] as const,
  detail: (id: string) => [...platformKeys.details(), id] as const,
};
